import { useRef, useCallback, useMemo, useState } from 'react';
import { NUMERIC_COLORMAPS } from './numericColormaps';
import { Button, Slider, Select, Tooltip, Popover } from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  FastBackwardOutlined,
  FastForwardOutlined,
  PushpinOutlined,
  CloudOutlined,
  GlobalOutlined,
  WifiOutlined,
  SettingOutlined,
  AimOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTheme } from '../../contexts/useTheme';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';
import type { TioTileField } from '../../api/tomorrowIoTilesService';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

// ============================================================================
// Temporal state helpers
// ============================================================================

type TemporalState = 'past' | 'now' | 'future';

const NOW_FUTURE_THRESHOLD_SECONDS = 90;
const NOW_PAST_THRESHOLD_SECONDS = 150;

function getTemporalState(displayTime: dayjs.Dayjs): TemporalState {
  const nowUtc = dayjs.utc();
  const displayUtc = displayTime.utc();
  const diffSeconds = displayUtc.diff(nowUtc, 'second');
  if (diffSeconds > NOW_FUTURE_THRESHOLD_SECONDS) return 'future';
  if (diffSeconds < -NOW_PAST_THRESHOLD_SECONDS) return 'past';
  return 'now';
}

function getTemporalColors(state: TemporalState, isDark: boolean) {
  switch (state) {
    case 'past':
      return { timeColor: isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.45)' };
    case 'now':
      return { timeColor: isDark ? '#95de64' : '#389e0d' };
    case 'future':
      return { timeColor: isDark ? '#69c0ff' : '#096dd9' };
  }
}

// ============================================================================
// Throttle constant for slider drag
// ============================================================================

const SLIDER_THROTTLE_MS = 50;

// ============================================================================
// Types
// ============================================================================

interface RadarTile {
  timestamp: string;
  url: string;
  datetime: dayjs.Dayjs;
  isNowcast?: boolean;
  forecastOffset?: number;
}

interface DockedPlayerPanelProps {
  selectedDateRange: { start: Date; end: Date } | null;
  observationsEnabled: boolean;
  tilesCount: number;
  mapTileProvider: MapTileProvider;
  onTomorrowIoSelect: () => void;
  onGlobalPrecipSelect: () => void;
  onRadarL2Select?: () => void;
  onRadarL2NumericSelect?: () => void;
  isRoot?: boolean;
  isEventActive: boolean;
  showTioMapProviders?: boolean;
  showTioFieldSelector: boolean;
  tioTileField: TioTileField;
  onTioTileFieldChange: (field: TioTileField) => void;
  radarOpacity: number;
  onRadarOpacityChange: (opacity: number) => void;
  tiles: RadarTile[];
  tioTimestamps: string[];
  currentIndex: number;
  tioCurrentIndex: number;
  isPlaying: boolean;
  isTioPlaying: boolean;
  fps: number;
  autoLoop: boolean;
  selectedEvent: { circuit?: { time_zone?: string } } | null;
  extendedTiles: RadarTile[];
  currentTile: RadarTile | undefined;
  onBackward: () => void;
  onForward: () => void;
  onPlayPause: () => void;
  onTioPlayPause: () => void;
  onCurrentIndexChange: (index: number) => void;
  onTioCurrentIndexChange: (index: number) => void;
  onFpsChange: (fps: number) => void;
  onAutoLoopChange: (autoLoop: boolean) => void;
  playbackDock: 'first' | 'current' | 'now' | 'last';
  onPlaybackDockChange: (dock: 'first' | 'current' | 'now' | 'last') => void;
  isLiveMode: boolean;
  loading: boolean;
  numericNoiseFloor?: number;
  onNumericNoiseFloorChange?: (value: number) => void;
  numericColormap?: string;
  onNumericColormapChange?: (value: string) => void;
  compact?: boolean;
  onCompactChange?: (compact: boolean) => void;
}

// ============================================================================
// Component
// ============================================================================

export const DockedPlayerPanel = ({
  selectedDateRange,
  observationsEnabled,
  tilesCount,
  mapTileProvider,
  onTomorrowIoSelect,
  onGlobalPrecipSelect,
  onRadarL2Select,
  onRadarL2NumericSelect,
  isRoot,
  isEventActive,
  showTioMapProviders = true,
  showTioFieldSelector,
  tioTileField,
  onTioTileFieldChange,
  radarOpacity,
  onRadarOpacityChange,
  tiles,
  tioTimestamps,
  currentIndex,
  tioCurrentIndex,
  isPlaying,
  isTioPlaying,
  fps,
  autoLoop,
  selectedEvent,
  extendedTiles,
  currentTile,
  onBackward,
  onForward,
  onPlayPause,
  onTioPlayPause,
  onCurrentIndexChange,
  onTioCurrentIndexChange,
  onFpsChange,
  onAutoLoopChange,
  playbackDock,
  onPlaybackDockChange,
  isLiveMode,
  loading,
  numericNoiseFloor,
  onNumericNoiseFloorChange,
  numericColormap,
  onNumericColormapChange,
  compact = false,
  onCompactChange,
}: DockedPlayerPanelProps) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [settingsOpen, setSettingsOpen] = useState(false);

  const lastUpdateRef = useRef<number>(0);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValueRef = useRef<number | null>(null);

  const circuitTz = selectedEvent?.circuit?.time_zone || 'UTC';
  const browserTimeZone = useMemo(() => dayjs.tz.guess(), []);
  const showBrowserTime = browserTimeZone !== circuitTz;

  const currentDisplayTime = useMemo(() => {
    if ((mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && currentTile) return currentTile.datetime;
    if (isTioProvider(mapTileProvider) && tioTimestamps[tioCurrentIndex]) {
      return dayjs.utc(tioTimestamps[tioCurrentIndex]).tz(circuitTz);
    }
    return null;
  }, [mapTileProvider, currentTile, tioTimestamps, tioCurrentIndex, circuitTz]);

  const temporalState = currentDisplayTime ? getTemporalState(currentDisplayTime) : null;
  const colors = temporalState ? getTemporalColors(temporalState, isDark) : null;

  const isNowcast = currentTile?.isNowcast || false;
  const forecastOffset = currentTile?.forecastOffset;

  const hasPlaybackData =
    ((mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && tiles.length > 0) ||
    (isTioProvider(mapTileProvider) && tioTimestamps.length > 0);

  const handleGoToNow = useCallback(() => {
    onPlaybackDockChange('now');
    const nowMs = Date.now();
    if (mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') {
      if (tiles.length === 0) return;
      let closestIdx = 0;
      let closestDiff = Infinity;
      tiles.forEach((tile, idx) => {
        const diff = Math.abs(tile.datetime.utc().valueOf() - nowMs);
        if (diff < closestDiff) { closestDiff = diff; closestIdx = idx; }
      });
      onCurrentIndexChange(closestIdx);
    } else {
      if (tioTimestamps.length === 0) return;
      let closestIdx = 0;
      let closestDiff = Infinity;
      tioTimestamps.forEach((ts, idx) => {
        const diff = Math.abs(new Date(ts).getTime() - nowMs);
        if (diff < closestDiff) { closestDiff = diff; closestIdx = idx; }
      });
      onTioCurrentIndexChange(closestIdx);
    }
  }, [mapTileProvider, tiles, tioTimestamps, onPlaybackDockChange, onCurrentIndexChange, onTioCurrentIndexChange]);

  const handleRadarSliderChange = useCallback(
    (value: number) => {
      const now = Date.now();
      const elapsed = now - lastUpdateRef.current;
      if (elapsed >= SLIDER_THROTTLE_MS || lastUpdateRef.current === 0) {
        lastUpdateRef.current = now;
        if (pendingTimeoutRef.current) {
          clearTimeout(pendingTimeoutRef.current);
          pendingTimeoutRef.current = null;
        }
        pendingValueRef.current = null;
        onCurrentIndexChange(value);
      } else {
        pendingValueRef.current = value;
        if (!pendingTimeoutRef.current) {
          pendingTimeoutRef.current = setTimeout(() => {
            pendingTimeoutRef.current = null;
            const toApply = pendingValueRef.current;
            pendingValueRef.current = null;
            if (toApply !== null) {
              lastUpdateRef.current = Date.now();
              onCurrentIndexChange(toApply);
            }
          }, SLIDER_THROTTLE_MS - elapsed);
        }
      }
    },
    [onCurrentIndexChange]
  );

  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';
  const mutedColor = isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)';

  const sectionHeight = compact ? 26 : 36;
  const sectionStyle: React.CSSProperties = {
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.8)',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
    borderRadius: compact ? 3 : 4,
    padding: compact ? '0 6px' : '0 8px',
    boxShadow: isDark ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.04)',
    height: sectionHeight,
    boxSizing: 'border-box',
  };

  const dockBtnStyle: React.CSSProperties = compact
    ? { minWidth: 22, padding: 0 }
    : { minWidth: 28, padding: '4px 6px' };

  const settingsContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 200 }}>
      {/* Opacity */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 500, color: mutedColor, marginBottom: 4 }}>
          Layer Opacity
        </div>
        <Slider
          min={0}
          max={1}
          step={0.1}
          value={radarOpacity}
          onChange={onRadarOpacityChange}
          tooltip={{ formatter: (value) => `${Math.round((value || 0) * 100)}%` }}
          style={{ margin: '4px 0 0' }}
        />
      </div>

      {/* TIO Field Selector */}
      {showTioFieldSelector && isTioProvider(mapTileProvider) && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 500, color: mutedColor, marginBottom: 4 }}>
            Weather Layer
          </div>
          <Select
            value={tioTileField}
            onChange={(value) => onTioTileFieldChange(value as TioTileField)}
            size="small"
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
          >
            <Option value="precipitationIntensity">Precipitation</Option>
            <Option value="rainIntensity">Rain</Option>
            <Option value="snowIntensity">Snow</Option>
            <Option value="temperature">Temperature</Option>
            <Option value="windSpeed">Wind Speed</Option>
            <Option value="windGust">Wind Gust</Option>
            <Option value="humidity">Humidity</Option>
            <Option value="cloudCover">Cloud Cover</Option>
            <Option value="visibility">Visibility</Option>
            <Option value="pressureSeaLevel">Pressure</Option>
          </Select>
        </div>
      )}

      {/* Numeric Radar Settings */}
      {mapTileProvider === 'radar-l2-numeric' && (
        <>
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: mutedColor, marginBottom: 4 }}>
              Colormap
            </div>
            <Select
              value={numericColormap}
              onChange={(v) => onNumericColormapChange?.(v)}
              size="small"
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              options={NUMERIC_COLORMAPS.map((c) => ({ value: c.id, label: c.name }))}
            />
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: mutedColor }}>
                Noise Floor
              </span>
              <span style={{ fontSize: 11, color: mutedColor }}>
                {(numericNoiseFloor ?? 0).toFixed(2)} mm/h
              </span>
            </div>
            <Slider
              min={0}
              max={2}
              step={0.05}
              value={numericNoiseFloor ?? 0}
              onChange={(v) => onNumericNoiseFloorChange?.(v)}
              tooltip={{ formatter: (value) => `${(value || 0).toFixed(2)} mm/h` }}
              style={{ margin: '4px 0 0' }}
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      className={compact ? 'docked-player-compact' : undefined}
      style={{
        flexShrink: 0,
        padding: '0',
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.02)',
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: sectionHeight,
        transition: 'min-height 0.15s ease',
      }}
    >
      <style>{`
        @keyframes docked-ball-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes nowDockPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(24, 144, 255, 0); }
        }
        ${compact ? `
          .docked-player-compact .ant-slider { margin: 4px 0; }
          .docked-player-compact .ant-slider-rail,
          .docked-player-compact .ant-slider-track { height: 3px !important; }
          .docked-player-compact .ant-slider-handle { width: 10px !important; height: 10px !important; }
          .docked-player-compact .ant-slider-handle::after { width: 10px !important; height: 10px !important; }
          .docked-player-compact .ant-select-selector { height: 22px !important; font-size: 10px !important; }
          .docked-player-compact .ant-select-selection-item { line-height: 20px !important; font-size: 10px !important; }
          .docked-player-compact .ant-btn-sm { height: 22px !important; width: 22px !important; min-width: 22px !important; padding: 0 !important; font-size: 11px !important; }
        ` : ''}
      `}</style>

      {/* ── Source selector ── */}
      {selectedDateRange && (
        <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', gap: compact ? 3 : 5, flexShrink: 0 }}>
          {!compact && (
            <span style={{ fontSize: 11, fontWeight: 500, color: mutedColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Source
            </span>
          )}
          <div style={{ display: 'flex', gap: 3 }}>
            {(observationsEnabled || !showTioMapProviders) && onRadarL2Select && (
              <Tooltip title={tilesCount === 0 ? 'Radar (No data available)' : 'Radar'}>
                <Button
                  type={mapTileProvider === 'radar-l2' ? 'primary' : 'default'}
                  size="small"
                  onClick={onRadarL2Select}
                  icon={<WifiOutlined rotate={90} />}
                />
              </Tooltip>
            )}
            {isRoot && observationsEnabled && onRadarL2NumericSelect && (
              <Tooltip title="Radar (Numeric - Beta)">
                <Button
                  type={mapTileProvider === 'radar-l2-numeric' ? 'primary' : 'default'}
                  size="small"
                  onClick={onRadarL2NumericSelect}
                  icon={<AimOutlined />}
                />
              </Tooltip>
            )}
            {showTioMapProviders && (
              <>
                <Tooltip title={isEventActive ? 'Global Precipitation' : 'Global Precipitation (only available for active events)'}>
                  <Button
                    type={mapTileProvider === 'tomorrow.io-global' ? 'primary' : 'default'}
                    size="small"
                    onClick={onGlobalPrecipSelect}
                    disabled={!isEventActive}
                    icon={<GlobalOutlined />}
                    style={{ opacity: !isEventActive ? 0.4 : 1 }}
                  />
                </Tooltip>
                <Tooltip title={isEventActive ? 'Tomorrow.io' : 'Tomorrow.io (only available for active events)'}>
                  <Button
                    type={mapTileProvider === 'tomorrow.io' ? 'primary' : 'default'}
                    size="small"
                    onClick={onTomorrowIoSelect}
                    disabled={!isEventActive}
                    icon={<CloudOutlined />}
                    style={{ opacity: !isEventActive ? 0.4 : 1 }}
                  />
                </Tooltip>
              </>
            )}
          </div>

          {/* Settings button (opacity, layer field) */}
          <Popover
            content={settingsContent}
            trigger="click"
            placement="bottomLeft"
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
          >
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              style={{ color: mutedColor }}
            />
          </Popover>
        </div>
      )}

      {/* ── Player (flexes to fill available space) ── */}
      {hasPlaybackData ? (
        <div style={{ ...sectionStyle, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: compact ? 4 : 6 }}>
          {/* Dock first */}
          {isEventActive && isLiveMode && (
            <>
              <Tooltip title={playbackDock === 'first' ? 'Dock to first tile on refresh (selected)' : 'Dock to first tile on refresh'}>
                <Button
                  type={playbackDock === 'first' ? 'primary' : 'default'}
                  size="small"
                  icon={<FastBackwardOutlined />}
                  onClick={() => {
                    onPlaybackDockChange('first');
                    if (mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') {
                      onCurrentIndexChange(0);
                    } else {
                      onTioCurrentIndexChange(0);
                    }
                  }}
                  style={dockBtnStyle}
                />
              </Tooltip>
              <Tooltip title={playbackDock === 'current' ? 'Stay on current tile on refresh (selected)' : 'Stay on current tile on refresh'}>
                <Button
                  type={playbackDock === 'current' ? 'primary' : 'default'}
                  size="small"
                  icon={<PushpinOutlined />}
                  onClick={() => onPlaybackDockChange('current')}
                  style={dockBtnStyle}
                />
              </Tooltip>
              <Tooltip title={playbackDock === 'now' ? 'Dock to current time on refresh (selected)' : 'Dock to current time on refresh'}>
                <Button
                  type={playbackDock === 'now' ? 'primary' : 'default'}
                  size="small"
                  icon={<AimOutlined />}
                  onClick={handleGoToNow}
                  style={{
                    ...dockBtnStyle,
                    ...(playbackDock === 'now' && temporalState !== 'now' ? {
                      animation: 'nowDockPulse 2s ease-in-out infinite',
                    } : {}),
                  }}
                />
              </Tooltip>
            </>
          )}

          {/* Transport controls */}
          {mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 2 : 3, flexShrink: 0 }}>
              <Button icon={<StepBackwardOutlined />} onClick={onBackward} disabled={currentIndex === 0} size="small" />
              <Button type="primary" icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={onPlayPause} size="small" />
              <Button icon={<StepForwardOutlined />} onClick={onForward} disabled={currentIndex === extendedTiles.length - 1} size="small" />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 2 : 3, flexShrink: 0 }}>
              <Button icon={<StepBackwardOutlined />} onClick={() => onTioCurrentIndexChange(Math.max(0, tioCurrentIndex - 1))} disabled={tioCurrentIndex === 0} size="small" />
              <Button type="primary" icon={isTioPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={onTioPlayPause} size="small" />
              <Button icon={<StepForwardOutlined />} onClick={() => onTioCurrentIndexChange(Math.min(tioTimestamps.length - 1, tioCurrentIndex + 1))} disabled={tioCurrentIndex === tioTimestamps.length - 1} size="small" />
            </div>
          )}

          {/* Slider */}
          <div style={{ flex: 1, minWidth: 0, marginLeft: compact ? 6 : 0 }}>
            {mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric' ? (
              <Slider
                min={0}
                max={extendedTiles.length - 1}
                value={currentIndex}
                onChange={handleRadarSliderChange}
                tooltip={{
                  formatter: (value) => {
                    const tile = extendedTiles[value || 0];
                    if (!tile) return '';
                    const timeStr = tile.datetime.format('HH:mm:ss');
                    return tile.isNowcast ? `${timeStr} (+${tile.forecastOffset}min)` : timeStr;
                  }
                }}
              />
            ) : (
              <Slider
                min={0}
                max={tioTimestamps.length - 1}
                value={tioCurrentIndex}
                onChange={onTioCurrentIndexChange}
                tooltip={{ formatter: (value) => dayjs.utc(tioTimestamps[value || 0]).tz(selectedEvent?.circuit?.time_zone || 'UTC').format('HH:mm') }}
              />
            )}
          </div>

          {/* FPS (radar only) */}
          {(mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && (
            <Select value={fps} onChange={onFpsChange} style={{ width: compact ? 68 : 76, flexShrink: 0, fontSize: compact ? 10 : undefined }} size="small">
              <Option value={1}>1 fps</Option>
              <Option value={2}>2 fps</Option>
              <Option value={5}>5 fps</Option>
            </Select>
          )}

          {/* Auto-loop toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 3 : 4, flexShrink: 0 }}>
            {!compact && (
              <span style={{ fontSize: 11, color: mutedColor }}>Loop</span>
            )}
            <Tooltip title={compact ? (autoLoop ? 'Disable loop' : 'Enable loop') : undefined}>
              <button
                onClick={() => onAutoLoopChange(!autoLoop)}
                style={{
                  width: compact ? 26 : 32,
                  height: compact ? 14 : 18,
                  borderRadius: compact ? 7 : 9,
                  border: 'none',
                  backgroundColor: autoLoop ? '#1890ff' : (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)'),
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                }}
                title={autoLoop ? 'Disable auto-loop' : 'Enable auto-loop'}
              >
                <div style={{
                  width: compact ? 10 : 14, height: compact ? 10 : 14, borderRadius: '50%', backgroundColor: '#fff',
                  position: 'absolute', top: 2, left: autoLoop ? (compact ? 14 : 16) : 2,
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                }} />
              </button>
            </Tooltip>
          </div>

          {/* Frame counter */}
          <span style={{ fontSize: compact ? 10 : 11, whiteSpace: 'nowrap', color: mutedColor, flexShrink: 0 }}>
            {mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric'
              ? `${currentIndex + 1}/${extendedTiles.length}`
              : `${tioCurrentIndex + 1}/${tioTimestamps.length}`}
          </span>

          {/* Dock last */}
          {isEventActive && isLiveMode && (
            <Tooltip title={playbackDock === 'last' ? 'Dock to last tile on refresh (selected)' : 'Dock to last tile on refresh'}>
              <Button
                type={playbackDock === 'last' ? 'primary' : 'default'}
                size="small"
                icon={<FastForwardOutlined />}
                onClick={() => {
                  onPlaybackDockChange('last');
                  if (mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') {
                    onCurrentIndexChange(Math.max(0, extendedTiles.length - 1));
                  } else {
                    onTioCurrentIndexChange(Math.max(0, tioTimestamps.length - 1));
                  }
                }}
                style={dockBtnStyle}
              />
            </Tooltip>
          )}
        </div>
      ) : (
        <div style={{ flex: 1 }} />
      )}

      {/* ── Time display ── */}
      {currentDisplayTime && colors && (
        <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', gap: compact ? 4 : 6, flexShrink: 0 }}>
          <span style={{
            fontSize: compact ? 13 : 20,
            fontWeight: compact ? 600 : 'bold',
            color: colors.timeColor,
            transition: 'color 0.3s ease',
            lineHeight: 1,
          }}>
            {currentDisplayTime.format('HH:mm')}
          </span>
          {showBrowserTime && (
            <span style={{ fontSize: compact ? 10 : 12, color: mutedColor, whiteSpace: 'nowrap' }}>
              ({currentDisplayTime.tz(browserTimeZone).format('HH:mm')}{!compact ? ' your time' : ''})
            </span>
          )}
          {isNowcast && forecastOffset !== undefined && (
            <span style={{
              fontSize: compact ? 9 : 10, fontWeight: 600, backgroundColor: '#1890ff', color: '#fff',
              padding: compact ? '0px 4px' : '1px 5px', borderRadius: compact ? 2 : 3, letterSpacing: '0.3px',
              lineHeight: compact ? '14px' : undefined,
            }}>
              +{forecastOffset}min
            </span>
          )}
          {temporalState === 'now' && (
            <span style={{
              fontSize: compact ? 8 : 9, fontWeight: 700, backgroundColor: '#52c41a', color: '#fff',
              padding: compact ? '0px 4px' : '1px 5px', borderRadius: compact ? 2 : 3, textTransform: 'uppercase', letterSpacing: '0.5px',
              lineHeight: compact ? '14px' : undefined,
            }}>
              LIVE
            </span>
          )}
        </div>
      )}

      {/* Compact toggle */}
      {onCompactChange && (
        <div
          style={{
            ...sectionStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            width: sectionHeight,
            flexShrink: 0,
          }}
        >
          <Tooltip title={compact ? 'Expand player' : 'Compact player'}>
            <Button
              type="text"
              size="small"
              icon={<ColumnHeightOutlined />}
              onClick={() => onCompactChange(!compact)}
              aria-label={compact ? 'Expand player' : 'Compact player'}
              style={compact
                ? { width: 20, height: 20, minWidth: 20, padding: 0, fontSize: 11, opacity: 0.7 }
                : { opacity: 0.7 }
              }
            />
          </Tooltip>
        </div>
      )}

      {/* ── Tomorrow.io Logo ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 5 : 8,
        flexShrink: 0,
        backgroundColor: isDark ? '#1a1a1a' : '#fff',
        padding: compact ? '0 6px' : '0 10px',
        borderRadius: compact ? 3 : 4,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
        height: sectionHeight,
        boxSizing: 'border-box',
      }}>
        <img
          src="/tio-ball.svg"
          alt=""
          style={{
            height: compact ? 18 : 34,
            width: compact ? 18 : 34,
            animation: loading ? 'docked-ball-spin 1.2s linear infinite' : 'none',
          }}
        />
        {!compact && (
          <img
            src={isDark ? '/tio-text-white.svg' : '/tio-text-dark.svg'}
            alt="Tomorrow.io"
            style={{ height: 28, opacity: 0.9 }}
          />
        )}
      </div>
    </div>
  );
};
